import NumberFlow from "@number-flow/react";
import { Minus, Plus } from "lucide-react";

type ArticleQuantityProps = {
	onChange?: (value: number) => void;
	quantity: number;
	maxQuantity: number;
	productPrice: number;
	disabled?: boolean;
	disabledReason?: React.ReactNode;
};

export function ArticleNonEditableQuantity({ quantity }: { quantity: number }) {
	return (
		<span className="text-xs font-bold text-muted-foreground inline-block text-center min-w-[6ch]">
			×{quantity}
		</span>
	);
}

export function ArticleQuantity({
	maxQuantity,
	quantity,
	onChange,
	disabled,
	disabledReason,
}: ArticleQuantityProps) {
	if (!onChange) return null;

	const handleChange = (delta: number) => {
		if (disabled) return;
		const newValue = Math.max(0, Math.min(maxQuantity, quantity + delta));
		if (newValue !== quantity) onChange(newValue);
	};

	return (
		<div className="flex flex-col gap-1 items-end">
			<div
				className={`bg-muted rounded-full items-center flex px-2 w-fit ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
			>
				<button
					type="button"
					className="py-2 rounded-full disabled:cursor-not-allowed"
					onClick={() => handleChange(-1)}
					disabled={disabled || quantity === 0}
				>
					<Minus className="size-4" />
				</button>
				<span className="text-xs font-bold text-muted-foreground inline-block text-center min-w-[6ch]">
					<NumberFlow value={quantity} suffix={` / ${maxQuantity}`} />
				</span>
				<button
					type="button"
					className="rounded-full py-2 disabled:cursor-not-allowed"
					onClick={() => handleChange(1)}
					disabled={disabled || quantity >= maxQuantity}
					title={disabled ? "Cannot add items already in another return" : undefined}
				>
					<Plus className="size-4" />
				</button>
			</div>
			{disabledReason && (
				<div className="text-xs text-destructive max-w-[200px]">
					{disabledReason}
				</div>
			)}
		</div>
	);
}
